import { useEffect } from 'react';
import { useAuth } from '@workos-inc/authkit-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { captureGclidFromUrl } from '../etc/gclid';
import { useLogout } from '../hooks/use-logout';
import { Button } from '@cotera/client/v0/components/ui/button';

export type WorkOsRedirectState =
  | {
      t: 'sign-in';
      returnTo?: string;
    }
  | {
      t: 'sign-up';
      referralCode: string | null;
    };

const WHOAMI_RETRY_KEY = 'cotera:whoami_retry_count';
const MAX_WHOAMI_RETRIES = 2;

/**
 * On redirect, the 'state' passed through the authentication flow will be of type RedirectState (the sign in variant).
 *
 * @param whoamiFailed - When true, we're showing login because whoami returned 401.
 */
export const WorkOSLogin: React.FC<{ whoamiFailed?: boolean }> = ({
  whoamiFailed = false,
}) => {
  const { signIn, user, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useLogout();

  useEffect(() => {
    if (isLoading) {
      return;
    }

    // If user is already authenticated, redirect to home instead of starting a new auth flow.
    if (user !== null && !whoamiFailed) {
      sessionStorage.removeItem(WHOAMI_RETRY_KEY);
      navigate('/', { replace: true });
      return;
    }

    // WorkOS says logged in but our API returned 401 — likely a transient session
    // issue (e.g. after switchToOrganization). Do a full page reload to / to get
    // fresh state. Use a counter to avoid infinite reload loops.
    if (user !== null && whoamiFailed) {
      const retries = Number(sessionStorage.getItem(WHOAMI_RETRY_KEY) ?? '0');
      if (retries < MAX_WHOAMI_RETRIES) {
        sessionStorage.setItem(WHOAMI_RETRY_KEY, String(retries + 1));
        window.location.href = '/';
        return;
      }
      // Exhausted retries — show manual recovery UI below
      return;
    }

    const searchParams = new URLSearchParams(location.search);
    const returnTo = searchParams.get('returnTo') ?? undefined;

    // CLI auth: store returnTo as backup (OAuth state can be unreliable)
    const isCliAuth = returnTo?.startsWith('/cli-login');
    if (returnTo !== undefined) {
      sessionStorage.setItem('cotera:cli_return_to', returnTo);
    }

    // If user is logged in but this is CLI auth, still go through WorkOS
    if (user !== null && !isCliAuth && !whoamiFailed) {
      sessionStorage.removeItem(WHOAMI_RETRY_KEY);
      navigate('/', { replace: true });
      return;
    }

    const redirectState: WorkOsRedirectState = { t: 'sign-in', returnTo };

    void signIn({ state: redirectState });
  }, [signIn, user, isLoading, navigate, whoamiFailed, location.search]);

  // Session truly invalid after retries: WorkOS says logged in but our API still returns 401
  if (whoamiFailed && user !== null) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-4">
        <p className="text-muted-foreground text-center text-sm">
          Your session could not be verified. Please sign out and try again.
        </p>
        <Button onClick={() => void logout()}>Sign out and try again</Button>
      </div>
    );
  }

  return null;
};

/**
 * On redirect, the 'state' passed through the authentication flow will be of type RedirectState (the sign up variant).
 */
export const WorkOSSignup: React.FC = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const referralCode = searchParams.get('referral_code');

  const { signUp } = useAuth();

  useEffect(() => {
    // Capture gclid before redirecting to WorkOS auth
    // Cookie will persist across the auth redirect
    captureGclidFromUrl();

    const redirectState: WorkOsRedirectState = {
      t: 'sign-up',
      referralCode,
    };

    void signUp({ state: redirectState });
  }, [signUp, referralCode]);

  return null;
};
